import React, { useEffect, useState } from "react"
import { formatDateLong } from "../../utils/format"
import { getGroupTheme } from "../../utils/groupTheme"

const toDateTimeLocalValue = (value) => {
  if (!value) return ""
  const dt = new Date(value)
  if (Number.isNaN(dt.getTime())) return ""
  const pad = (n) => String(n).padStart(2, "0")
  const year = dt.getFullYear()
  const month = pad(dt.getMonth() + 1)
  const day = pad(dt.getDate())
  const hour = pad(dt.getHours())
  const minute = pad(dt.getMinutes())
  return `${year}-${month}-${day}T${hour}:${minute}`
}

const toUtcWallClockIso = (value) => {
  const normalized = String(value || "").trim()
  if (!normalized) return null
  const [datePart, timePartRaw = "00:00"] = normalized.split("T")
  if (!datePart) return null
  const timePart =
    timePartRaw.length === 5
      ? `${timePartRaw}:00`
      : timePartRaw.length >= 8
        ? timePartRaw.slice(0, 8)
        : "00:00:00"
  const iso = `${datePart}T${timePart}.000Z`
  const dt = new Date(iso)
  if (Number.isNaN(dt.getTime())) return null
  return dt.toISOString()
}

function GroupChamaaSimulatorPanel({
  group,
  isAdmin = false,
  onUpdateSimulatorConfig,
  onAdvanceSimulator,
  isUpdatingSimulator = false,
  isAdvancingSimulator = false,
  readOnly = false,
}) {
  const theme = getGroupTheme(group)
  const simulator = group?.simulator || {}
  const simulatorEnabled = !!simulator?.enabled
  const simulatorNow = simulator?.now ? new Date(simulator.now) : null
  const groupId = group?.id || null

  const [simulatedTimeInput, setSimulatedTimeInput] = useState("")
  const [localError, setLocalError] = useState("")
  const [localSuccess, setLocalSuccess] = useState("")

  useEffect(() => {
    setSimulatedTimeInput(toDateTimeLocalValue(simulator?.now))
  }, [simulator?.now])

  const canRunActions = simulatorEnabled && !isUpdatingSimulator && !isAdvancingSimulator

  const runWithResult = async (runner, fallbackMessage) => {
    if (readOnly) return
    setLocalError("")
    setLocalSuccess("")
    const result = await runner()
    if (!result?.ok) {
      setLocalError(result?.message || "Simulator action failed.")
      return
    }
    setLocalSuccess(result?.message || fallbackMessage)
  }

  const handleToggleSimulator = async () => {
    await runWithResult(
      () =>
        onUpdateSimulatorConfig?.({
          groupId,
          enabled: !simulatorEnabled,
          now: simulatorEnabled ? simulator?.now || null : new Date().toISOString(),
        }),
      simulatorEnabled ? "Simulator disabled." : "Simulator enabled."
    )
  }

  const handleSetSimulatedTime = async () => {
    if (!simulatedTimeInput) {
      setLocalError("Select a simulated date and time first.")
      return
    }
    const iso = toUtcWallClockIso(simulatedTimeInput)
    if (!iso) {
      setLocalError("Simulated date/time is invalid.")
      return
    }
    await runWithResult(
      () =>
        onUpdateSimulatorConfig?.({
          groupId,
          enabled: true,
          now: iso,
        }),
      "Simulated time updated."
    )
  }

  const handleAdvance = async (mode) => {
    await runWithResult(
      () => onAdvanceSimulator?.({ groupId, mode }),
      "Simulator advanced."
    )
  }

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {readOnly && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Archived view is read-only. Reactivate the group to edit simulator settings.
        </div>
      )}
      <fieldset
        disabled={readOnly}
        className={`min-w-0 border-0 p-0 m-0 ${readOnly ? "opacity-75" : ""}`}
      >
      <div className={`bg-white/95 rounded-2xl border shadow-md p-4 space-y-4 ${theme.border}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Simulator</h3>
            <p className="text-xs text-gray-500">
              Test Chamaa rounds/cycles quickly by advancing simulated group time.
            </p>
          </div>
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border ${
              simulatorEnabled
                ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                : "bg-gray-100 text-gray-600 border-gray-200"
            }`}
          >
            {simulatorEnabled ? "Enabled" : "Disabled"}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <p className="text-gray-500">Real Time</p>
            <p className="font-semibold text-gray-900">{formatDateLong(new Date())}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <p className="text-gray-500">Simulated Time</p>
            <p className={`font-semibold ${theme.text}`}>
              {simulatorNow ? formatDateLong(simulatorNow) : "-"}
            </p>
          </div>
        </div>

        {!isAdmin ? (
          <div className={`rounded-lg border text-xs px-3 py-2 ${theme.bg} ${theme.text} ${theme.border}`}>
            Simulator is managed by the group admin. Any simulated timeline changes apply to all members.
          </div>
        ) : (
          <>
            <div className={`rounded-xl border p-3 space-y-3 ${theme.border} ${theme.mutedBg}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className={`text-[10px] font-bold uppercase tracking-wide ${theme.text}`}>
                  Simulator Controls
                </p>
                <button
                  type="button"
                  onClick={handleToggleSimulator}
                  disabled={isUpdatingSimulator || isAdvancingSimulator}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide text-white disabled:opacity-60 transition-all ${
                    simulatorEnabled ? "bg-gray-600 hover:bg-gray-700 shadow-sm" : theme.button
                  }`}
                >
                  {isUpdatingSimulator
                    ? "Saving..."
                    : simulatorEnabled
                      ? "Disable Simulator"
                      : "Enable Simulator"}
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-end">
                <div>
                  <label className={`block text-[10px] font-bold uppercase tracking-wide mb-1 ${theme.text}`}>
                    Simulated Date/Time
                  </label>
                  <input
                    type="datetime-local"
                    value={simulatedTimeInput}
                    onChange={(e) => setSimulatedTimeInput(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg text-xs focus:ring-2 outline-none bg-white ${theme.border} ${theme.ring}`}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSetSimulatedTime}
                  disabled={!simulatorEnabled || isUpdatingSimulator || isAdvancingSimulator}
                  className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide text-white disabled:opacity-50 shadow-sm transition-all ${theme.button}`}
                >
                  Set Time
                </button>
              </div>
            </div>

            <div className={`rounded-xl border ${theme.border} ${theme.bgMuted50} p-3 space-y-3`}>
              <p className={`text-[10px] font-bold uppercase tracking-wide ${theme.text}`}>
                Tested Flow (Use This)
              </p>
              <ol className={`list-decimal list-inside text-[11px] ${theme.accentText} space-y-1`}>
                <li>Enable simulator.</li>
                <li>Set simulated time to any date (even in future).</li>
                <li>Advance to any desired stage.</li>
              </ol>
              <button
                type="button"
                onClick={() => handleAdvance("auto")}
                disabled={!canRunActions}
                className={`px-3 py-2 rounded-lg text-[10px] font-bold uppercase tracking-wide text-white disabled:opacity-50 shadow-sm transition-all ${theme.button}`}
              >
                {isAdvancingSimulator ? "Processing..." : "Auto Advance to Stage"}
              </button>
              <div className={`rounded-lg border ${theme.border} bg-white px-3 py-2 text-[11px] ${theme.accentText} space-y-1`}>
                <p className="font-semibold opacity-80">What happens after click:</p>
                <p className="text-[10px] opacity-70">
                  The system will process all rounds between current simulated time and next cycle start.
                </p>
              </div>
            </div>
          </>
        )}

        {!!localError && (
          <div className="border border-red-300 bg-red-50 text-red-700 text-xs p-2 rounded-lg">
            {localError}
          </div>
        )}
        {!!localSuccess && (
          <div className="border border-green-300 bg-green-50 text-green-700 text-xs p-2 rounded-lg">
            {localSuccess}
          </div>
        )}
      </div>
      </fieldset>
    </div>
  )
}

export default GroupChamaaSimulatorPanel
